import { useQuery } from '@apollo/client'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { AssetActivityLogDocument } from 'generated/resources/graphql'
import moment from 'moment'

import i18n from 'modules/core/lib/i18n'

interface ActivityLogProps {
  assetId: string
}

interface ActivityLogItem {
  id: string
  updatedAt: string
  action?: string | null
  status?: string | null
  ownerId?: number | null
  locationId?: number | null
  serialNumber?: string | null
  purchasePriceCents?: number | null
  currency?: string | null
  label?: string | null
  deletedAt?: string | null
  author?: {
    id: number
    firstName?: string | null
    lastName?: string | null
  } | null
}

const ActivityLog = ({ assetId }: ActivityLogProps) => {
  const { data, loading, error } = useQuery(AssetActivityLogDocument, {
    variables: { assetId },
    skip: !assetId,
    fetchPolicy: 'no-cache', // Disable caching to prevent object reference issues
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Spinner size="small" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 text-sm text-red-600">
        {i18n.t('it_management.asset_details.activity_log_error')}
      </div>
    )
  }

  const activityLogsConnection = data?.itManagement?.assetActivityLogsConnection
  const activities: ActivityLogItem[] = activityLogsConnection?.nodes || []

  if (activities.length === 0) {
    return (
      <div className="p-4 text-sm text-f1-foreground-secondary">
        {i18n.t('it_management.asset_details.no_activity')}
      </div>
    )
  }

  const getActivityDescription = (activity: ActivityLogItem) => {
    if (activity.deletedAt) {
      return i18n.t('it_management.asset_details.activity_deleted')
    }

    if (activity.action) {
      switch (activity.action) {
        case 'deleted':
          return i18n.t('it_management.asset_details.activity_deleted')
        case 'created':
          return i18n.t('it_management.asset_details.activity_created')
        case 'updated':
          return i18n.t('it_management.asset_details.activity_updated')
        case 'ownership_changed':
          return i18n.t('it_management.asset_details.activity_ownership_changed')
        case 'status_changed':
          return i18n.t('it_management.asset_details.activity_status_changed')
        default:
          return i18n.t('it_management.asset_details.activity_updated')
      }
    }

    return i18n.t('it_management.asset_details.activity_updated')
  }

  const getActivityIcon = (activity: ActivityLogItem) => {
    if (activity.deletedAt) {
      return '🗑️'
    }

    switch (activity.status) {
      case 'in_stock':
        return '📦'
      case 'assigned':
        return '👤'
      case 'maintenance':
        return '🔧'
      case 'retired':
        return '🏁'
      default:
        return '📝'
    }
  }

  return (
    <div className="flex flex-col">
      {activities.map((activity, index) => (
        <div
          key={`activity-${activity.updatedAt}-${activity.action}-${activity.status}-${activity.ownerId}-${activity.locationId}-${index}`}
          className="flex mb-6"
        >
          <div className="flex flex-col items-center mr-4">
            <div className="w-2 h-2 bg-f1-foreground rounded-full flex-shrink-0 flex items-center justify-center text-xs">
              {getActivityIcon(activity)}
            </div>
            {index < activities.length - 1 && (
              <div
                className="w-px flex-1 min-h-8"
                style={{ backgroundColor: 'rgba(5, 35, 72, 0.14)' }}
              />
            )}
          </div>
          <div className="flex flex-col flex-1">
            <label className="text-f1-foreground-secondary mb-2 -top-1.5 relative">
              {moment(activity.updatedAt).format('Do MMMM YYYY')}
            </label>
            <p className="font-medium mb-2">{getActivityDescription(activity)}</p>
            <p className="text-sm text-f1-foreground-secondary mb-1">
              {activity.author
                ? activity.author.firstName + ' ' + activity.author.lastName + ' · '
                : ''}{' '}
              {moment(activity.updatedAt).format('HH:mm')}
            </p>
          </div>
        </div>
      ))}
    </div>
  )
}

export default ActivityLog
